import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
    Radar,
    Loader2,
    MapPin,
    Phone,
    Globe,
    Sparkles,
    Check,
    AlertCircle,
    Package,
    ChevronDown,
    Info,
    Lock,
    Store,
    Network,
    Rocket,
    Mail,
    FileUp,
    Link2,
    Bot,
    Map as MapIcon,
    Search,
    ExternalLink,
    Bookmark,
    Compass,
    ArrowLeft,
    RefreshCw,
    Users,
    ArrowUpRight,
    Plus,
    X,
    Filter,
} from "lucide-react";
import { useCustomerData } from "./CustomerDataContext";
import { GROWTHOS_BASE, authHeaders } from "../config/api";

/* ==========================================================
   LEAD GENERATION
   Opened from the sidebar, scoped to whichever product the user
   clicked into from Home (Home.jsx / PricingPage.jsx / Growthosdashboard.jsx
   all persist that product's id under localStorage key
   "growthos_active_product_id" as the user moves through the flow).

   Three sources are live right now, and they return two DIFFERENT
   kinds of results — shown in their own sections below:

   - OpenStreetMap: queries the free OSM Overpass API for REAL,
     currently-mapped businesses matching the product's Category
     near its Target Location. Verified to exist; contact info is
     often sparse (crowd-sourced map data).

   - AI Search: sends the product's own details (name, description,
     category, target audience/location) to Groq's free LLM and asks
     it to research and propose likely prospect organizations, with
     richer fields (decision maker, designation, company size,
     pain points, lead score, reasoning). This is AI-INFERRED from
     general knowledge, NOT verified real-time data — shown with
     a clear disclaimer.

   - LinkedIn Companies: there's no paid LinkedIn/Sales Navigator API
     wired up here, so instead of a live LinkedIn lookup, checking
     this source reveals a text box where the user pastes in company
     names and/or LinkedIn company URLs (copied straight out of
     LinkedIn's own search results). Each one is then AI-researched
     via the same Groq pattern as AI Search, and the results land in
     the same prospect table shape — same "AI-inferred, not verified"
     disclaimer applies.

   Everything else (Google Maps, Google Business, Apollo.io,
   Hunter.io, CSV Upload, Website URL, Custom Search) needs a paid
   API key or separate build that isn't connected yet, so those
   render as locked "Coming soon" cards.
========================================================== */

const PRODUCTS_ENDPOINT = `${GROWTHOS_BASE}/products`;
const DISCOVER_ENDPOINT = `${GROWTHOS_BASE}/leads/discover`;
// n8n workflow: Lead Generation Webhook -> Normalize Request -> Build Overpass
// Query -> OpenStreetMap Overpass -> Normalize OSM Leads -> Verify Website ->
// Groq Qualification -> Finalize Qualification -> Keep Qualified Leads ->
// Prepare CRM Lead -> Save to GrowthOS CRM -> Build Response -> Return to
// Frontend. Replaces the direct /leads/discover call for the OpenStreetMap
// source. Test URL for now — swap to the production n8n URL when it's live.
const N8N_LEAD_GENERATION_WEBHOOK =
    "https://isfdemo.app.n8n.cloud/webhook/growthos/lead-generation";
const AI_PROSPECTS_ENDPOINT = `${GROWTHOS_BASE}/leads/ai-prospects`;
// LinkedIn Companies source — user pastes in company names/URLs copied
// from LinkedIn, we send them server-side to be AI-researched (same
// Groq pattern as AI Search) and get back prospect rows in the same shape.
const LINKEDIN_COMPANIES_ENDPOINT = `${GROWTHOS_BASE}/leads/linkedin-companies`;
const CREATE_LEAD_ENDPOINT = `${GROWTHOS_BASE}/leads`;
const LEADS_LIST_ENDPOINT = `${GROWTHOS_BASE}/leads`;
const VERIFY_URL_ENDPOINT = `${GROWTHOS_BASE}/verify-url`;

// Lead Generation Hierarchy — Sector -> Category -> Subcategory. Backs the
// cascading filter dropdowns + "Add Lead Manually" modal below. See
// lead_hierarchy_endpoints.py / isfathena_lead_hierarchy_schema.sql.
const SECTORS_ENDPOINT = `${GROWTHOS_BASE}/leads/sectors`; // GET list, POST add-custom ("Other")
const CATEGORIES_BY_SECTOR_ENDPOINT = `${GROWTHOS_BASE}/leads/sectors`; // + /{sector_id}/categories
const SUBCATEGORIES_BY_CATEGORY_ENDPOINT = `${GROWTHOS_BASE}/leads/categories`; // + /{category_id}/subcategories
const SEARCH_LEADS_ENDPOINT = `${GROWTHOS_BASE}/leads/search`;
const ADD_LEAD_HIERARCHY_ENDPOINT = `${GROWTHOS_BASE}/leads/hierarchy-add`;
// + /{sector_id}/ai-categories (POST) — AI-generates Category/Subcategory
// options for a sector that doesn't have any yet, and saves them.
const AI_GENERATE_CATEGORIES_ENDPOINT = (sectorId) => `${SECTORS_ENDPOINT}/${sectorId}/ai-categories`;
// AI Search scoped to the Sector/Category/Subcategory filter instead of
// a product — the filter's own "Generate Leads" action.
const AI_PROSPECTS_BY_HIERARCHY_ENDPOINT = `${GROWTHOS_BASE}/leads/ai-prospects-by-hierarchy`;

// The "Saved Leads" card used to only show leads that originated from
// THIS page (Lead Generation), identified by lead_source being one of
// these values (the same ones set in addOsmLeadToCrm / addProspectToCrm
// below). That's why a lead's Active/Inactive status — which works fine
// everywhere else — never showed up here for leads sourced from Website,
// Facebook, Google Ads, etc.: they were filtered out before the
// Active/Inactive split even ran. Saved Leads now mirrors Lead
// Management and shows every saved lead regardless of source, so this
// list is unused but kept for reference / in case source-scoping is
// wanted again later.
const GENERATED_LEAD_SOURCES = ["OpenStreetMap", "AI Search"];

const ACTIVE_PRODUCT_KEY = "growthos_active_product_id";

// Themed scrollbar — same treatment as Lead Management's, so both
// screens' scrollbars (page-level and the results tables) match the
// dark/orange theme instead of the browser default. See the longer
// comment on this same constant in Lead_Management.js.
const THEMED_SCROLLBAR =
    "[&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar]:h-1.5 " +
    "[&::-webkit-scrollbar-track]:bg-transparent " +
    "[&::-webkit-scrollbar-thumb]:bg-orange-500/20 [&::-webkit-scrollbar-thumb]:rounded-full " +
    "[&::-webkit-scrollbar-thumb:hover]:bg-orange-500/40 " +
    "[scrollbar-width:thin] [scrollbar-color:rgba(249,115,22,0.25)_transparent]";

// Horizontal scrollbar styling for unsaved cards layout — orange themed to match the app
const HORIZONTAL_SCROLLBAR =
    "[&::-webkit-scrollbar]:h-3 " +
    "[&::-webkit-scrollbar-track]:bg-transparent " +
    "[&::-webkit-scrollbar-thumb]:bg-orange-500/70 [&::-webkit-scrollbar-thumb]:rounded-full " +
    "[&::-webkit-scrollbar-thumb:hover]:bg-orange-500 " +
    "[scrollbar-width:auto] [scrollbar-color:rgba(249,115,22,0.7)_transparent]";

// Every request here is company-scoped via this JWT — same pattern as
// Home.jsx and every other module that talks to growthos_backend.
// Only these two actually fetch data right now — everything else in this
// list needs a paid API key / separate integration that isn't wired up
// yet, so they render as locked "Coming soon" cards.
const LEAD_SOURCES = [
    { id: "google_maps", label: "Google Maps", icon: MapPin, enabled: false },
    { id: "google_business", label: "Google Business", icon: Store, enabled: false },
    { id: "linkedin", label: "LinkedIn Companies", icon: Network, enabled: true },
    { id: "apollo", label: "Apollo.io", icon: Rocket, enabled: false },
    { id: "hunter", label: "Hunter.io", icon: Mail, enabled: false },
    { id: "csv_upload", label: "CSV Upload", icon: FileUp, enabled: false },
    { id: "website_url", label: "Website URL", icon: Link2, enabled: false },
    { id: "ai_search", label: "AI Search", icon: Bot, enabled: true },
    { id: "openstreetmap", label: "OpenStreetMap", icon: MapIcon, enabled: true },
    { id: "custom_search", label: "Custom Search", icon: Search, enabled: false },
];

const PROSPECT_FIELDS = [
    { key: "company_name", label: "Company" },
    { key: "website", label: "Website" },
    { key: "decision_maker", label: "Decision Maker" },
    { key: "designation", label: "Designation" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "linkedin", label: "LinkedIn" },
    { key: "location", label: "Location" },
    { key: "industry", label: "Industry" },
    { key: "company_size", label: "Company Size" },
    { key: "estimated_scale", label: "Estimated Scale" },
    { key: "technology_used", label: "Technology Used" },
    { key: "pain_points", label: "Pain Points" },
    { key: "lead_score", label: "Lead Score" },
    { key: "reason", label: "Reason" },
];

// Given a set of prospect/lead rows, returns only the PROSPECT_FIELDS whose
// key has a non-empty value in at least one row. "company_name" always stays
// visible (it's the sticky identity column), everything else is hidden if
// every row in the current result set is blank for it — e.g. AI Search often
// doesn't return "linkedin" or "technology_used", so those columns shouldn't
// take up table width showing nothing but "—" for every row.
const getVisibleProspectFields = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return PROSPECT_FIELDS;
    return PROSPECT_FIELDS.filter((f) => {
        if (f.key === "company_name") return true;
        return rows.some((row) => {
            const val = row?.[f.key];
            return val !== null && val !== undefined && String(val).trim() !== "";
        });
    });
};

// AI Search prospects' websites are AI-inferred, not real, live-verified
// data — the domain frequently doesn't exist. This checks it for real,
// right when clicked, instead of opening a dead tab.
const VerifiedWebsiteLink = ({ url, className }) => {
    const [checking, setChecking] = useState(false);
    const [unreachable, setUnreachable] = useState(false);

    const handleClick = async (e) => {
        e.preventDefault();
        if (checking) return;
        setChecking(true);
        setUnreachable(false);
        try {
            const res = await fetch(`${VERIFY_URL_ENDPOINT}?url=${encodeURIComponent(url)}`);
            const data = await res.json();
            if (data.reachable) {
                window.open(data.checked_url, "_blank", "noopener,noreferrer");
            } else {
                setUnreachable(true);
                setTimeout(() => setUnreachable(false), 3500);
            }
        } catch (err) {
            setUnreachable(true);
            setTimeout(() => setUnreachable(false), 3500);
        } finally {
            setChecking(false);
        }
    };

    return (
        <span className="inline-flex flex-col">
            <button type="button" onClick={handleClick} disabled={checking} className={className}>
                <span className="truncate max-w-[150px]">{url}</span>
                {checking ? (
                    <Loader2 size={10} className="shrink-0 animate-spin" />
                ) : (
                    <ExternalLink size={10} className="shrink-0" />
                )}
            </button>
            {unreachable && <span className="text-red-400 text-[10px] mt-0.5">Site unreachable — not opened</span>}
        </span>
    );
};

const LeadGenerationSection = () => {
    const navigate = useNavigate();
    const { fetchLeads } = useCustomerData();

    // null = show the two entry cards, "saved" = Saved Leads list,
    // "unsaved" = the lead discovery flow (everything that used to be
    // the whole page).
    const [activeView, setActiveView] = useState(null);

    const [savedLeads, setSavedLeads] = useState([]);
    const [savedLoading, setSavedLoading] = useState(false);
    const [savedError, setSavedError] = useState("");
    const [savedFetched, setSavedFetched] = useState(false);
    // "" = All Products. Otherwise a product id — same dropdown pattern
    // as the main "Product" selector used in the discovery flow below.
    const [savedProductId, setSavedProductId] = useState("");
    // "all" | "active" | "inactive" — Active = replied and the AI (or a
    // manual override) read it as interested. Inactive = contacted and
    // either never replied or replied not-interested. Leads that haven't
    // been contacted yet ("pending") show up under "all" only, same as
    // before this feature existed.
    const [savedInterestTab, setSavedInterestTab] = useState("all");

    const [products, setProducts] = useState([]);
    const [productsLoading, setProductsLoading] = useState(true);
    const [selectedProductId, setSelectedProductId] = useState(
        () => localStorage.getItem(ACTIVE_PRODUCT_KEY) || ""
    );

    const [selectedSources, setSelectedSources] = useState(() => new Set());

    // OpenStreetMap results (real, verified-to-exist places).
    const [discovered, setDiscovered] = useState([]);
    // AI Search results (AI-inferred prospect research, richer fields).
    const [prospects, setProspects] = useState([]);
    // LinkedIn Companies — user-pasted company names/URLs (one per line
    // or comma-separated) and the AI-researched results for them. Same
    // prospect shape/table as AI Search, just scoped to named companies
    // instead of the product's target audience.
    const [linkedinCompaniesInput, setLinkedinCompaniesInput] = useState("");
    const [linkedinProspects, setLinkedinProspects] = useState([]);

    const [searchLoading, setSearchLoading] = useState(false);
    const [searchError, setSearchError] = useState("");
    const [hasSearched, setHasSearched] = useState(false);

    // Per-lead state so each "Add to CRM" button can show its own
    // in-flight/added status independent of the others. Keyed across both
    // result types since the keys themselves don't collide.
    const [addingKey, setAddingKey] = useState(null);
    const [addedKeys, setAddedKeys] = useState(() => new Set());

    // Lead Generation Hierarchy — Sector -> Category -> Subcategory filter
    // + manual add, scoped to whichever product is selected above.
    const [sectors, setSectors] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [selectedSector, setSelectedSector] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedSubcategory, setSelectedSubcategory] = useState(null);
    const [hierarchyLeads, setHierarchyLeads] = useState([]);
    const [loadingHierarchyLeads, setLoadingHierarchyLeads] = useState(false);
    const [hierarchyLoading, setHierarchyLoading] = useState(false);
    // True while AI is proposing Category/Subcategory options for a sector
    // that doesn't have any yet (see the "Load categories" effect below).
    const [generatingCategories, setGeneratingCategories] = useState(false);

    // "Other" sector flow — user types a custom sector name instead of
    // picking a pre-seeded one; it's POSTed to SECTORS_ENDPOINT and saved,
    // so it shows up in the dropdown itself from then on.
    const [showOtherSectorInput, setShowOtherSectorInput] = useState(false);
    const [otherSectorName, setOtherSectorName] = useState("");
    const [addingSector, setAddingSector] = useState(false);
    const [addSectorError, setAddSectorError] = useState("");

    // AI-generated leads from the Sector/Category/Subcategory filter
    // itself (independent of any product) — the filter's own "Generate
    // Leads" results, separate from hierarchyLeads (which only searches
    // leads already saved in the CRM).
    const [filterProspects, setFilterProspects] = useState([]);
    const [filterProspectsLoading, setFilterProspectsLoading] = useState(false);
    const [filterProspectsError, setFilterProspectsError] = useState("");

    const [showAddModal, setShowAddModal] = useState(false);
    const [addingManualLead, setAddingManualLead] = useState(false);
    const emptyManualLeadForm = {
        company_name: "",
        phone: "",
        email: "",
        location: "",
        sector_id: null,
        category_id: null,
        subcategory_id: null,
        product_id: null,
        decision_maker: "",
        designation: "",
        company_size: "",
        pain_points: "",
        lead_score: 0,
    };
    const [manualLeadForm, setManualLeadForm] = useState(emptyManualLeadForm);

    useEffect(() => {
        (async () => {
            setProductsLoading(true);
            try {
                const res = await fetch(PRODUCTS_ENDPOINT, { method: "GET", headers: authHeaders() });
                const resp = await res.json().catch(() => null);
                const list = res.ok && resp?.success && Array.isArray(resp.data) ? resp.data : [];
                setProducts(list);
                setSelectedProductId((prev) => {
                    const stillExists = list.some((p) => String(p.id) === String(prev));
                    if (stillExists) return prev;
                    return list.length ? String(list[0].id) : "";
                });
            } catch (err) {
                setProducts([]);
            } finally {
                setProductsLoading(false);
            }
        })();
    }, []);

    // Load sectors once on mount.
    useEffect(() => {
        (async () => {
            setHierarchyLoading(true);
            try {
                const res = await fetch(SECTORS_ENDPOINT, { method: "GET", headers: authHeaders() });
                const resp = await res.json().catch(() => null);
                setSectors(res.ok && resp?.success && Array.isArray(resp.data) ? resp.data : []);
            } catch (err) {
                setSectors([]);
            } finally {
                setHierarchyLoading(false);
            }
        })();
    }, []);

    // Load categories whenever the selected sector changes.
    useEffect(() => {
        if (!selectedSector) {
            setCategories([]);
            setSelectedCategory(null);
            setSubcategories([]);
            setSelectedSubcategory(null);
            return;
        }
        (async () => {
            setHierarchyLoading(true);
            try {
                const res = await fetch(`${CATEGORIES_BY_SECTOR_ENDPOINT}/${selectedSector}/categories`, {
                    method: "GET",
                    headers: authHeaders(),
                });
                const resp = await res.json().catch(() => null);
                let cats = res.ok && resp?.success && Array.isArray(resp.data) ? resp.data : [];

                // No categories saved for this sector yet — ask AI to
                // propose a Category/Subcategory taxonomy and save it,
                // instead of leaving the user with an empty dropdown.
                if (cats.length === 0) {
                    setGeneratingCategories(true);
                    try {
                        const aiRes = await fetch(AI_GENERATE_CATEGORIES_ENDPOINT(selectedSector), {
                            method: "POST",
                            headers: authHeaders(),
                        });
                        const aiResp = await aiRes.json().catch(() => null);
                        if (aiRes.ok && aiResp?.success && Array.isArray(aiResp.data)) {
                            cats = aiResp.data;
                        }
                    } catch (err) {
                        // Leave categories empty — the dropdown just stays
                        // unpopulated and the user can retry by re-picking the sector.
                    } finally {
                        setGeneratingCategories(false);
                    }
                }

                setCategories(cats);
                setSelectedCategory(null);
                setSubcategories([]);
                setSelectedSubcategory(null);
            } catch (err) {
                setCategories([]);
            } finally {
                setHierarchyLoading(false);
            }
        })();
    }, [selectedSector]);

    // Load subcategories whenever the selected category changes.
    useEffect(() => {
        if (!selectedCategory) {
            setSubcategories([]);
            setSelectedSubcategory(null);
            return;
        }
        (async () => {
            setHierarchyLoading(true);
            try {
                const res = await fetch(`${SUBCATEGORIES_BY_CATEGORY_ENDPOINT}/${selectedCategory}/subcategories`, {
                    method: "GET",
                    headers: authHeaders(),
                });
                const resp = await res.json().catch(() => null);
                setSubcategories(res.ok && resp?.success && Array.isArray(resp.data) ? resp.data : []);
                setSelectedSubcategory(null);
            } catch (err) {
                setSubcategories([]);
            } finally {
                setHierarchyLoading(false);
            }
        })();
    }, [selectedCategory]);

    // Re-run the hierarchy-filtered lead search whenever any filter (or the
    // active product) changes. Skips the call entirely if nothing's picked.
    useEffect(() => {
        if (!selectedSector && !selectedCategory && !selectedSubcategory) {
            setHierarchyLeads([]);
            return;
        }
        (async () => {
            setLoadingHierarchyLeads(true);
            try {
                const params = new URLSearchParams();
                if (selectedSector) params.append("sector_id", selectedSector);
                if (selectedCategory) params.append("category_id", selectedCategory);
                if (selectedSubcategory) params.append("subcategory_id", selectedSubcategory);
                // Deliberately NOT scoped by product — this filter searches
                // saved leads purely by Sector/Category/Subcategory.

                const res = await fetch(`${SEARCH_LEADS_ENDPOINT}?${params.toString()}`, {
                    method: "GET",
                    headers: authHeaders(),
                });
                const resp = await res.json().catch(() => null);
                setHierarchyLeads(res.ok && resp?.success && Array.isArray(resp.data) ? resp.data : []);
            } catch (err) {
                setHierarchyLeads([]);
            } finally {
                setLoadingHierarchyLeads(false);
            }
        })();
    }, [selectedSector, selectedCategory, selectedSubcategory]);

    const selectedProduct = useMemo(
        () => products.find((p) => String(p.id) === String(selectedProductId)) || null,
        [products, selectedProductId]
    );

    // Columns to actually render for each results table — a column is
    // dropped if every currently-visible row has no data for it, so e.g.
    // AI Search results without any "linkedin" or "technology_used" don't
    // show an all-dash column. Recomputed whenever that table's data changes.
    const visibleFilterFields = useMemo(() => getVisibleProspectFields(filterProspects), [filterProspects]);
    const visibleProspectFields = useMemo(() => getVisibleProspectFields(prospects), [prospects]);
    const visibleLinkedinFields = useMemo(() => getVisibleProspectFields(linkedinProspects), [linkedinProspects]);

    // Splits the LinkedIn Companies text box into a clean list — one
    // company name/URL per line, or comma-separated, trimmed, empties dropped.
    const linkedinCompanyNames = useMemo(
        () =>
            linkedinCompaniesInput
                .split(/[\n,]/)
                .map((s) => s.trim())
                .filter(Boolean),
        [linkedinCompaniesInput]
    );

    // Saved Leads list narrowed down to whichever product is chosen in
    // that view's own dropdown ("" = All Products = no filtering).
    const filteredSavedLeads = useMemo(() => {
        let list = savedLeads;
        if (savedProductId) {
            list = list.filter((l) => String(l.product_id ?? "") === String(savedProductId));
        }
        if (savedInterestTab === "active") {
            list = list.filter((l) => l.interest_status === "active");
        } else if (savedInterestTab === "inactive") {
            list = list.filter((l) => l.interest_status === "inactive");
        }
        return list;
    }, [savedLeads, savedProductId, savedInterestTab]);

    // Counts shown on the tab pills — computed off the product-filtered
    // list (not savedInterestTab-filtered) so switching tabs doesn't
    // change the other tabs' numbers.
    const savedInterestCounts = useMemo(() => {
        const base = savedProductId
            ? savedLeads.filter((l) => String(l.product_id ?? "") === String(savedProductId))
            : savedLeads;
        return {
            all: base.length,
            active: base.filter((l) => l.interest_status === "active").length,
            inactive: base.filter((l) => l.interest_status === "inactive").length,
        };
    }, [savedLeads, savedProductId]);

    // Switching products clears previous results/sources instead of
    // showing stale leads under a different product's targeting info.
    const handleSelectProduct = (id) => {
        setSelectedProductId(id);
        localStorage.setItem(ACTIVE_PRODUCT_KEY, id);
        resetResults();
    };

    const resetResults = () => {
        setDiscovered([]);
        setProspects([]);
        setHasSearched(false);
        setSearchError("");
        setAddedKeys(new Set());
    };

    // Pulls every lead already saved to the CRM — same full list Lead
    // Management shows, no source filtering — so the Active/Inactive tabs
    // here reflect every lead's real interest_status, not just the ones
    // this page happened to discover.
    const fetchSavedLeads = async () => {
        setSavedLoading(true);
        setSavedError("");
        try {
            const res = await fetch(LEADS_LIST_ENDPOINT, { method: "GET", headers: authHeaders() });
            const resp = await res.json().catch(() => null);
            const list = res.ok && resp?.success && Array.isArray(resp.data) ? resp.data : [];
            setSavedLeads(list);
        } catch (err) {
            setSavedError("Couldn't load saved leads. Please try again.");
        } finally {
            setSavedLoading(false);
            setSavedFetched(true);
        }
    };

    const openSavedLeads = () => {
        setActiveView("saved");
        if (!savedFetched) fetchSavedLeads();
    };

    const toggleSource = (source) => {
        if (!source.enabled) return; // locked/"Coming soon" — not selectable yet
        setSelectedSources((prev) => {
            const next = new Set(prev);
            if (next.has(source.id)) next.delete(source.id);
            else next.add(source.id);
            return next;
        });
    };

    // Sends the product's own details to Groq (free) and asks it to
    // research likely prospects — NOT real-time verified data, just what
    // a knowledgeable researcher could put together as a starting point.
    // Goes through OUR backend so the API key stays server-side instead
    // of in the JS bundle, same pattern as the OpenStreetMap source.
    const fetchAiProspects = async (product) => {
        const res = await fetch(`${AI_PROSPECTS_ENDPOINT}?product_id=${product.id}`, {
            method: "GET",
            headers: authHeaders(),
        });
        const resp = await res.json().catch(() => null);
        if (!res.ok || !resp?.success) {
            throw new Error(resp?.message || "Couldn't reach AI Search.");
        }
        return Array.isArray(resp.data) ? resp.data : [];
    };

    // LinkedIn Companies — sends the user-pasted company names/URLs to
    // our backend, which AI-researches each one via Groq (server-side,
    // same key-never-in-the-bundle pattern as AI Search) and returns
    // prospect rows in the same shape as AI Search's.
    const fetchLinkedinCompanyProspects = async (product, companyNames) => {
        const res = await fetch(LINKEDIN_COMPANIES_ENDPOINT, {
            method: "POST",
            headers: { "Content-Type": "application/json", ...authHeaders() },
            body: JSON.stringify({
                company_names: companyNames,
                product_id: product?.id ?? null,
            }),
        });
        const resp = await res.json().catch(() => null);
        if (!res.ok || !resp?.success) {
            throw new Error(resp?.message || "Couldn't research LinkedIn companies.");
        }
        return Array.isArray(resp.data) ? resp.data : [];
    };

    const findLeads = async () => {
        if (!selectedProduct || selectedSources.size === 0) return;
        if (selectedSources.has("linkedin") && linkedinCompanyNames.length === 0) {
            setSearchError("Enter at least one LinkedIn company name or URL.");
            return;
        }
        setSearchLoading(true);
        setSearchError("");
        setHasSearched(true);
        setAddedKeys(new Set());
        setDiscovered([]);
        setProspects([]);
        setLinkedinProspects([]);

        if (selectedSources.has("openstreetmap")) {
            try {
                console.log("[Lead Gen] Starting OpenStreetMap search for:", selectedProduct.product_name);
                
                const res = await fetch(N8N_LEAD_GENERATION_WEBHOOK, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", ...authHeaders() },
                    body: JSON.stringify({
                        product_id: selectedProduct.id,
                        product_name: selectedProduct.product_name,
                        description: selectedProduct.description || "",
                        category: selectedProduct.category,
                        target_audience: selectedProduct.target_audience || "",
                        target_location: selectedProduct.target_location,
                        target_keywords: selectedProduct.target_keywords || "",
                    }),
                });

                const rawText = await res.text();
                console.log("[Lead Gen] n8n response status:", res.status);
                console.log("[Lead Gen] n8n response length:", rawText.length);
                console.log("[Lead Gen] n8n response preview:", rawText.substring(0, 500));

                let resp = null;
                
                // Enhanced error detection
                if (!rawText || rawText.trim().length === 0) {
                    throw new Error("OpenStreetMap workflow returned empty response. The Overpass API may be overloaded or the location not found.");
                }

                // Try to parse as JSON
                try {
                    resp = JSON.parse(rawText);
                } catch (parseErr) {
                    console.error("[Lead Gen] JSON parse failed:", parseErr);
                    console.error("[Lead Gen] Raw response that failed to parse:", rawText);
                    
                    // Check if response contains error indicators
                    if (rawText.toLowerCase().includes('error')) {
                        throw new Error(
                            `Overpass API error: ${rawText.substring(0, 300)}`
                        );
                    }
                    if (rawText.toLowerCase().includes('<!doctype') || rawText.includes('<html')) {
                        throw new Error(
                            "Overpass API returned HTML (likely an error page). The server may be overloaded or the query invalid."
                        );
                    }
                    if (rawText.includes('timeout')) {
                        throw new Error(
                            "Overpass API timeout: The location is too large or the query too complex. Try a smaller area or fewer criteria."
                        );
                    }
                    
                    throw new Error(
                        `Invalid JSON from OpenStreetMap workflow. Response: ${rawText.substring(0, 200)}`
                    );
                }

                // Validate response structure
                if (!resp) {
                    throw new Error("OpenStreetMap workflow returned null response.");
                }

                if (!res.ok) {
                    throw new Error(resp?.message || `OpenStreetMap workflow failed with status ${res.status}`);
                }

                if (!resp.success) {
                    throw new Error(
                        resp?.message || 
                        `OpenStreetMap workflow returned success:false. Response: ${JSON.stringify(resp).substring(0, 200)}`
                    );
                }

                const leads = Array.isArray(resp.leads) ? resp.leads : [];
                console.log(`[Lead Gen] Found ${leads.length} qualified leads`);
                
                if (leads.length === 0) {
                    console.warn("[Lead Gen] No leads found. This could mean: no businesses in that area, or Overpass API returned no results.");
                }

                const mapped = leads.map((l) => ({
                    osm_id: l.osm_id,
                    user_name: l.company_name,
                    user_address: l.address || l.location || "",
                    user_mobile_number: l.phone || "",
                    user_email: l.email || "",
                    web_url: l.website || "",
                    gmb_status: "Uncategorized",
                    lead_source: "OpenStreetMap + n8n",
                    ai_score: l.ai_score,
                    priority: l.priority,
                }));
                
                setDiscovered(mapped);
                setAddedKeys((prev) => {
                    const next = new Set(prev);
                    mapped.forEach((l) => next.add(String(l.osm_id ?? l.user_name)));
                    return next;
                });
                
                console.log("[Lead Gen] OpenStreetMap search completed successfully");

            } catch (err) {
                console.error("[Lead Gen] OpenStreetMap error:", err);
                setSearchError(
                    err.message || 
                    "Could not generate leads from OpenStreetMap. Please try a different location or category."
                );
            }
        }

        if (selectedSources.has("ai_search")) {
            try {
                const results = await fetchAiProspects(selectedProduct);
                setProspects(results);
            } catch (err) {
                setSearchError(err.message || "Couldn't reach AI Search. Please try again.");
            }
        }

        if (selectedSources.has("linkedin")) {
            try {
                const results = await fetchLinkedinCompanyProspects(selectedProduct, linkedinCompanyNames);
                setLinkedinProspects(results);
            } catch (err) {
                setSearchError(err.message || "Couldn't research LinkedIn companies. Please try again.");
            }
        }

        setSearchLoading(false);
    };

    const addOsmLeadToCrm = async (lead) => {
        const key = String(lead.osm_id ?? lead.user_name);
        setAddingKey(key);
        try {
            const res = await fetch(CREATE_LEAD_ENDPOINT, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...authHeaders() },
                body: JSON.stringify({
                    user_name: lead.user_name,
                    user_address: lead.user_address,
                    user_email: lead.user_email,
                    user_mobile_number: lead.user_mobile_number,
                    web_url: lead.web_url,
                    country: lead.country,
                    gmb_status: lead.gmb_status || "Uncategorized",
                    lead_source: lead.lead_source || "OpenStreetMap",
                    current_status: "New",
                    prospect_comment: selectedProduct
                        ? `Discovered for product: ${selectedProduct.product_name}`
                        : "",
                    product_id: selectedProduct?.id ?? null,
                }),
            });
            const resp = await res.json().catch(() => null);
            if (res.ok && resp?.success) {
                setAddedKeys((prev) => new Set(prev).add(key));
                setSavedFetched(false); // Saved Leads list is now stale — refetch next time it opens.
                fetchLeads();
            }
        } catch (err) {
            // Leave the button in its non-added state — the user can retry.
        } finally {
            setAddingKey(null);
        }
    };

    const addProspectToCrm = async (prospect, index) => {
        const key = `gpt-${index}-${prospect.company_name}`;
        setAddingKey(key);
        try {
            const res = await fetch(CREATE_LEAD_ENDPOINT, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...authHeaders() },
                body: JSON.stringify({
                    user_name: prospect.company_name,
                    user_address: prospect.location || "",
                    user_email: prospect.email || "",
                    user_mobile_number: prospect.phone || "",
                    web_url: prospect.website || "",
                    country: "",
                    gmb_status: "Uncategorized",
                    lead_source: "AI Search",
                    current_status: "New",
                    prospect_comment: selectedProduct
                        ? `Discovered by AI Search for product: ${selectedProduct.product_name}`
                        : "AI-generated prospect — verify details before outreach.",
                    product_id: selectedProduct?.id ?? null,
                    // Carries this exact score into Lead Management's "AI
                    // Score" column instead of it being recomputed there.
                    ai_score: Number.isFinite(Number(prospect.lead_score))
                        ? Math.round(Number(prospect.lead_score))
                        : null,
                    // Rich AI Search fields have their own dedicated columns
                    // in Lead Management — sent here directly instead of
                    // being packed into prospect_comment (which is what
                    // was silently dropping all of this before).
                    decision_maker: prospect.decision_maker || "",
                    designation: prospect.designation || "",
                    linkedin_url: prospect.linkedin || "",
                    industry: prospect.industry || "",
                    company_size: prospect.company_size || "",
                    estimated_scale: prospect.estimated_scale || "",
                    technology_used: prospect.technology_used || "",
                    pain_points: prospect.pain_points || "",
                    reason: prospect.reason || "",
                }),
            });
            const resp = await res.json().catch(() => null);
            if (res.ok && resp?.success) {
                setAddedKeys((prev) => new Set(prev).add(key));
                setSavedFetched(false); // Saved Leads list is now stale — refetch next time it opens.
                fetchLeads();
            }
        } catch (err) {
            // Leave the button in its non-added state — the user can retry.
        } finally {
            setAddingKey(null);
        }
    };

    const addLinkedinProspectToCrm = async (prospect, index) => {
        const key = `linkedin-${index}-${prospect.company_name}`;
        setAddingKey(key);
        try {
            const res = await fetch(CREATE_LEAD_ENDPOINT, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...authHeaders() },
                body: JSON.stringify({
                    user_name: prospect.company_name,
                    user_address: prospect.location || "",
                    user_email: prospect.email || "",
                    user_mobile_number: prospect.phone || "",
                    web_url: prospect.website || "",
                    country: "",
                    gmb_status: "Uncategorized",
                    lead_source: "LinkedIn Companies",
                    current_status: "New",
                    prospect_comment: selectedProduct
                        ? `Discovered via LinkedIn Companies for product: ${selectedProduct.product_name}`
                        : "LinkedIn-sourced prospect — verify details before outreach.",
                    product_id: selectedProduct?.id ?? null,
                    ai_score: Number.isFinite(Number(prospect.lead_score))
                        ? Math.round(Number(prospect.lead_score))
                        : null,
                    decision_maker: prospect.decision_maker || "",
                    designation: prospect.designation || "",
                    linkedin_url: prospect.linkedin || "",
                    industry: prospect.industry || "",
                    company_size: prospect.company_size || "",
                    estimated_scale: prospect.estimated_scale || "",
                    technology_used: prospect.technology_used || "",
                    pain_points: prospect.pain_points || "",
                    reason: prospect.reason || "",
                }),
            });
            const resp = await res.json().catch(() => null);
            if (res.ok && resp?.success) {
                setAddedKeys((prev) => new Set(prev).add(key));
                setSavedFetched(false); // Saved Leads list is now stale — refetch next time it opens.
                fetchLeads();
            }
        } catch (err) {
            // Leave the button in its non-added state — the user can retry.
        } finally {
            setAddingKey(null);
        }
    };

    // Re-runs the hierarchy search with the current filters — used both by
    // the auto-refresh effect above and to refresh right after a manual add.
    const refreshHierarchyLeads = async () => {
        if (!selectedSector && !selectedCategory && !selectedSubcategory) return;
        setLoadingHierarchyLeads(true);
        try {
            const params = new URLSearchParams();
            if (selectedSector) params.append("sector_id", selectedSector);
            if (selectedCategory) params.append("category_id", selectedCategory);
            if (selectedSubcategory) params.append("subcategory_id", selectedSubcategory);

            const res = await fetch(`${SEARCH_LEADS_ENDPOINT}?${params.toString()}`, {
                method: "GET",
                headers: authHeaders(),
            });
            const resp = await res.json().catch(() => null);
            setHierarchyLeads(res.ok && resp?.success && Array.isArray(resp.data) ? resp.data : []);
        } catch (err) {
            // Leave the current list as-is — the user can retry via the filters.
        } finally {
            setLoadingHierarchyLeads(false);
        }
    };

    // Adds a custom sector via the "Other" option — typed by the user
    // instead of picked from the pre-seeded list. Saved server-side so it
    // shows up in the dropdown itself (via GET /leads/sectors) next time.
    const handleAddOtherSector = async () => {
        const name = otherSectorName.trim();
        if (!name) return;
        setAddingSector(true);
        setAddSectorError("");
        try {
            const res = await fetch(SECTORS_ENDPOINT, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...authHeaders() },
                body: JSON.stringify({ sector_name: name }),
            });
            const resp = await res.json().catch(() => null);
            if (res.ok && resp?.success && resp.data?.[0]) {
                const newSector = resp.data[0];
                setSectors((prev) => {
                    if (prev.some((s) => s.id === newSector.id)) return prev;
                    return [...prev, newSector].sort((a, b) => a.sector_name.localeCompare(b.sector_name));
                });
                setSelectedSector(newSector.id);
                setShowOtherSectorInput(false);
                setOtherSectorName("");
            } else {
                setAddSectorError(resp?.message || "Could not add sector.");
            }
        } catch (err) {
            setAddSectorError("Could not add sector. Please try again.");
        } finally {
            setAddingSector(false);
        }
    };

    // Generates leads purely from the Sector/Category/Subcategory filter
    // via AI — no product involved. This is the filter's own "Generate
    // Leads" action, separate from the product-scoped "Find Leads" above.
    const generateLeadsFromFilter = async () => {
        if (!selectedSector) {
            setFilterProspectsError("Select at least a Sector first.");
            return;
        }
        setFilterProspectsLoading(true);
        setFilterProspectsError("");
        try {
            const params = new URLSearchParams();
            params.append("sector_id", selectedSector);
            if (selectedCategory) params.append("category_id", selectedCategory);
            if (selectedSubcategory) params.append("subcategory_id", selectedSubcategory);

            const res = await fetch(`${AI_PROSPECTS_BY_HIERARCHY_ENDPOINT}?${params.toString()}`, {
                method: "GET",
                headers: authHeaders(),
            });
            const resp = await res.json().catch(() => null);
            if (res.ok && resp?.success) {
                setFilterProspects(Array.isArray(resp.data) ? resp.data : []);
            } else {
                setFilterProspects([]);
                setFilterProspectsError(resp?.message || "Could not generate leads for this filter.");
            }
        } catch (err) {
            setFilterProspects([]);
            setFilterProspectsError("Could not generate leads. Please try again.");
        } finally {
            setFilterProspectsLoading(false);
        }
    };

    // Adds one AI-generated filter prospect straight to the CRM, tagged
    // with the current Sector/Category/Subcategory — deliberately no
    // product_id, since this flow is filter-only by design.
    const addFilterProspectToCrm = async (prospect, index) => {
        const key = `filter-${index}-${prospect.company_name}`;
        setAddingKey(key);
        try {
            const res = await fetch(ADD_LEAD_HIERARCHY_ENDPOINT, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...authHeaders() },
                body: JSON.stringify({
                    company_name: prospect.company_name,
                    phone: prospect.phone || "",
                    email: prospect.email || "",
                    location: prospect.location || "",
                    sector_id: selectedSector,
                    category_id: selectedCategory,
                    subcategory_id: selectedSubcategory,
                    lead_source: "AI Search (Filter)",
                    decision_maker: prospect.decision_maker || "",
                    designation: prospect.designation || "",
                    company_size: prospect.company_size || "",
                    pain_points: prospect.pain_points || "",
                    lead_score: Number.isFinite(Number(prospect.lead_score))
                        ? Math.round(Number(prospect.lead_score))
                        : 0,
                }),
            });
            const resp = await res.json().catch(() => null);
            if (res.ok && resp?.success) {
                setAddedKeys((prev) => new Set(prev).add(key));
                setSavedFetched(false);
                fetchLeads();
                await refreshHierarchyLeads();
            }
        } catch (err) {
            // Leave the button in its non-added state — the user can retry.
        } finally {
            setAddingKey(null);
        }
    };

    const openAddManualLeadModal = () => {
        setManualLeadForm({
            ...emptyManualLeadForm,
            sector_id: selectedSector ?? null,
            category_id: selectedCategory ?? null,
            subcategory_id: selectedSubcategory ?? null,
            product_id: selectedProduct?.id ?? null,
        });
        setShowAddModal(true);
    };

    const handleAddManualLead = async () => {
        if (!manualLeadForm.company_name.trim()) {
            setSearchError("Company name is required to add a lead manually.");
            return;
        }

        setAddingManualLead(true);
        try {
            const res = await fetch(ADD_LEAD_HIERARCHY_ENDPOINT, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...authHeaders() },
                body: JSON.stringify({
                    ...manualLeadForm,
                    product_id: manualLeadForm.product_id ?? selectedProduct?.id ?? null,
                }),
            });
            const resp = await res.json().catch(() => null);
            if (!res.ok || !resp?.success) {
                throw new Error(resp?.message || "Failed to add lead");
            }

            setShowAddModal(false);
            setManualLeadForm(emptyManualLeadForm);
            setSavedFetched(false); // Saved Leads list is now stale — refetch next time it opens.
            fetchLeads();
            await refreshHierarchyLeads();
        } catch (err) {
            setSearchError(err.message || "Failed to add lead. Please try again.");
        } finally {
            setAddingManualLead(false);
        }
    };

    const canSearch =
        Boolean(selectedProduct) &&
        selectedSources.size > 0 &&
        (!selectedSources.has("linkedin") || linkedinCompanyNames.length > 0);

    // Sector -> Category -> Subcategory cascading filter, shown above the
    // hierarchy results table in the "unsaved" (discovery) view.
    const HierarchyFilter = () => (
        <div className="bg-black border border-white/10 rounded-2xl p-5 mb-4">
            <div className="flex items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2.5">
                    <span className="h-8 w-8 rounded-full bg-orange-500/15 flex items-center justify-center shrink-0">
                        <Filter size={15} className="text-orange-500" />
                    </span>
                    <div>
                        <h3 className="text-sm font-semibold text-white">Filters</h3>
                        <p className="text-[11px] text-gray-500">Generate leads by Sector / Category / Subcategory</p>
                    </div>
                </div>
            </div>

            <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                <div className="flex-1 min-w-0">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                            <label className="text-[11px] font-medium text-gray-500 mb-1 block">Sector</label>
                            <select
                                value={showOtherSectorInput ? "other" : (selectedSector || "")}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    if (v === "other") {
                                        setShowOtherSectorInput(true);
                                        setAddSectorError("");
                                        setSelectedSector(null);
                                    } else {
                                        setShowOtherSectorInput(false);
                                        setSelectedSector(v ? parseInt(v, 10) : null);
                                    }
                                }}
                                disabled={hierarchyLoading}
                                className="w-full bg-black border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-orange-500/60 disabled:opacity-50"
                            >
                                <option value="">-- Select Sector --</option>
                                {sectors.map((s) => (
                                    <option key={s.id} value={s.id}>{s.sector_name}</option>
                                ))}
                                <option value="other">Other (type your own)</option>
                            </select>

                            {showOtherSectorInput && (
                                <div className="mt-2 flex gap-2">
                                    <input
                                        type="text"
                                        value={otherSectorName}
                                        onChange={(e) => setOtherSectorName(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                e.preventDefault();
                                                handleAddOtherSector();
                                            }
                                        }}
                                        placeholder="Type new sector name"
                                        className="flex-1 min-w-0 bg-black border border-orange-500/30 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-orange-500"
                                        autoFocus
                                    />
                                    <button
                                        type="button"
                                        onClick={handleAddOtherSector}
                                        disabled={addingSector || !otherSectorName.trim()}
                                        className="shrink-0 flex items-center gap-1 text-xs font-semibold bg-orange-500 hover:bg-orange-600 disabled:opacity-40 text-white px-3 py-1.5 rounded-lg"
                                    >
                                        {addingSector ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
                                        Add
                                    </button>
                                </div>
                            )}
                            {addSectorError && <p className="text-[11px] text-red-400 mt-1">{addSectorError}</p>}
                        </div>

                        <div>
                            <label className="text-[11px] font-medium text-gray-500 mb-1 block">Category</label>
                            <select
                                value={selectedCategory || ""}
                                onChange={(e) => setSelectedCategory(e.target.value ? parseInt(e.target.value, 10) : null)}
                                disabled={hierarchyLoading || generatingCategories || !selectedSector}
                                className="w-full bg-black border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-orange-500/60 disabled:opacity-50"
                            >
                                <option value="">
                                    {!selectedSector ? "-- Select Sector First --" : "-- Select Category --"}
                                </option>
                                {categories.map((c) => (
                                    <option key={c.id} value={c.id}>{c.category_name}</option>
                                ))}
                            </select>
                            {generatingCategories && (
                                <p className="text-[11px] text-orange-400 mt-1 flex items-center gap-1">
                                    <Loader2 size={11} className="animate-spin" /> AI is generating categories...
                                </p>
                            )}
                        </div>

                        <div>
                            <label className="text-[11px] font-medium text-gray-500 mb-1 block">Subcategory</label>
                            <select
                                value={selectedSubcategory || ""}
                                onChange={(e) => setSelectedSubcategory(e.target.value ? parseInt(e.target.value, 10) : null)}
                                disabled={hierarchyLoading || !selectedCategory}
                                className="w-full bg-black border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-orange-500/60 disabled:opacity-50"
                            >
                                <option value="">
                                    {!selectedCategory ? "-- Select Category First --" : "-- Select Subcategory --"}
                                </option>
                                {subcategories.map((sc) => (
                                    <option key={sc.id} value={sc.id}>{sc.subcategory_name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {(selectedSector || selectedCategory || selectedSubcategory) && (
                        <div className="mt-3 flex gap-2 flex-wrap items-center">
                            {selectedSector && (
                                <span className="inline-flex items-center gap-1.5 text-xs bg-orange-500/10 border border-orange-500/40 text-orange-400 pl-2.5 pr-1.5 py-1 rounded-full">
                                    Sector: {sectors.find((s) => s.id === selectedSector)?.sector_name}
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setSelectedSector(null);
                                            setShowOtherSectorInput(false);
                                        }}
                                        className="text-orange-400/70 hover:text-white"
                                    >
                                        <X size={11} />
                                    </button>
                                </span>
                            )}
                            {selectedCategory && (
                                <span className="inline-flex items-center gap-1.5 text-xs bg-orange-500/10 border border-orange-500/40 text-orange-400 pl-2.5 pr-1.5 py-1 rounded-full">
                                    Category: {categories.find((c) => c.id === selectedCategory)?.category_name}
                                    <button
                                        type="button"
                                        onClick={() => setSelectedCategory(null)}
                                        className="text-orange-400/70 hover:text-white"
                                    >
                                        <X size={11} />
                                    </button>
                                </span>
                            )}
                            {selectedSubcategory && (
                                <span className="inline-flex items-center gap-1.5 text-xs bg-orange-500/10 border border-orange-500/40 text-orange-400 pl-2.5 pr-1.5 py-1 rounded-full">
                                    Subcategory: {subcategories.find((sc) => sc.id === selectedSubcategory)?.subcategory_name}
                                    <button
                                        type="button"
                                        onClick={() => setSelectedSubcategory(null)}
                                        className="text-orange-400/70 hover:text-white"
                                    >
                                        <X size={11} />
                                    </button>
                                </span>
                            )}
                            <button
                                type="button"
                                onClick={() => {
                                    setSelectedSector(null);
                                    setSelectedCategory(null);
                                    setSelectedSubcategory(null);
                                    setShowOtherSectorInput(false);
                                    setFilterProspects([]);
                                    setFilterProspectsError("");
                                }}
                                className="text-xs text-gray-500 hover:text-orange-400 underline"
                            >
                                Clear all
                            </button>
                        </div>
                    )}
                </div>

                {/* Small pill button, matches the reference layout — sits to
                    the right of the filters instead of stretched full-width. */}
                <button
                    type="button"
                    onClick={generateLeadsFromFilter}
                    disabled={!selectedSector || filterProspectsLoading}
                    className="shrink-0 flex items-center justify-center gap-1.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-semibold px-4 py-2 rounded-full transition whitespace-nowrap"
                >
                    {filterProspectsLoading ? (
                        <Loader2 size={13} className="animate-spin" />
                    ) : (
                        <Sparkles size={13} />
                    )}
                    {filterProspectsLoading ? "Generating..." : "Generate Leads"}
                </button>
            </div>

            {!selectedSector && (
                <p className="text-[11px] text-gray-600 mt-2">Select at least a Sector to generate leads.</p>
            )}
            {filterProspectsError && (
                <p className="text-[11px] text-red-400 mt-2">{filterProspectsError}</p>
            )}
        </div>
    );

    return (
        <div id="sec-lead-generation" className={`h-full flex flex-col py-6 px-4 sm:px-6 lg:px-8 overflow-y-auto overflow-x-hidden ${THEMED_SCROLLBAR}`}>
            <button
                type="button"
                onClick={() => navigate("/app/dashboard-preview")}
                className="shrink-0 self-start mb-4 inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium bg-white/5 border border-white/10 text-gray-300 hover:text-orange-400 hover:border-orange-600/40 hover:bg-orange-600/10 transition"
            >
                <ArrowLeft size={15} /> Back
            </button>
            {/* Header */}
            <div className="shrink-0">
                <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2">
                    <Radar className="text-orange-500" size={26} />
                    Lead Generation
                </h1>
                <p className="text-gray-400 mt-1.5 text-sm">
                    Choose a lead source, then discover businesses matching a product's target audience.
                </p>
            </div>

            {/* Entry point: two cards — Saved Leads / Unsaved Leads */}
            {activeView === null && (
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-3xl shrink-0">
                    <button
                        type="button"
                        onClick={openSavedLeads}
                        className="group relative text-left rounded-2xl border border-orange-600/20 bg-black p-6 hover:border-orange-500/60 transition"
                    >
                        {/* Corner accent — matches the Media page card style */}
                        <span className="pointer-events-none absolute top-3 right-3 h-3 w-3 border-t border-r border-orange-500/40" />

                        <div className="flex items-start justify-between">
                            <div className="h-11 w-11 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center group-hover:bg-orange-500/20 transition">
                                <Bookmark size={20} className="text-orange-500" />
                            </div>
                            <span className="text-[11px] font-semibold tracking-widest uppercase text-gray-500">
                                CRM
                            </span>
                        </div>

                        <h3 className="text-white font-semibold mt-4">Saved Leads</h3>
                        <p className="text-gray-400 text-xs mt-1.5 leading-relaxed">
                            Leads you've already discovered here and added to your CRM.
                        </p>

                        <div className="flex items-center justify-between mt-5 pt-4 border-t border-white/10">
                            <span className="flex items-center gap-1.5 text-xs text-gray-400">
                                <span className="h-1.5 w-1.5 rounded-full bg-green-500 shrink-0" />
                                Ready
                            </span>
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-white/[0.06] group-hover:bg-white/10 border border-white/10 rounded-full px-3 py-1.5 transition">
                                Open <ArrowUpRight size={13} />
                            </span>
                        </div>
                    </button>

                    <button
                        type="button"
                        onClick={() => setActiveView("unsaved")}
                        className="group relative text-left rounded-2xl border border-orange-600/20 bg-black p-6 hover:border-orange-500/60 transition"
                    >
                        <span className="pointer-events-none absolute top-3 right-3 h-3 w-3 border-t border-r border-orange-500/40" />

                        <div className="flex items-start justify-between">
                            <div className="h-11 w-11 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center group-hover:bg-orange-500/20 transition">
                                <Compass size={20} className="text-orange-500" />
                            </div>
                            <span className="text-[11px] font-semibold tracking-widest uppercase text-gray-500">
                                Discovery
                            </span>
                        </div>

                        <h3 className="text-white font-semibold mt-4">Unsaved Leads</h3>
                        <p className="text-gray-400 text-xs mt-1.5 leading-relaxed">
                            Discover new businesses via AI Search or OpenStreetMap and add them to your CRM.
                        </p>

                        <div className="flex items-center justify-between mt-5 pt-4 border-t border-white/10">
                            <span className="flex items-center gap-1.5 text-xs text-gray-400">
                                <span className="h-1.5 w-1.5 rounded-full bg-green-500 shrink-0" />
                                Ready
                            </span>
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-white/[0.06] group-hover:bg-white/10 border border-white/10 rounded-full px-3 py-1.5 transition">
                                Open <ArrowUpRight size={13} />
                            </span>
                        </div>
                    </button>
                </div>
            )}

            {/* Saved Leads view */}
            {activeView === "saved" && (
                <div className="mt-6 flex-1 min-h-0 flex flex-col">
                    <button
                        type="button"
                        onClick={() => setActiveView(null)}
                        className="shrink-0 inline-flex items-center gap-1.5 text-xs font-medium text-gray-400 hover:text-orange-400 transition w-fit"
                    >
                        <ArrowLeft size={14} /> Back
                    </button>

                    <div className="flex items-center justify-between mt-4 shrink-0 gap-3 flex-wrap">
                        <div className="flex items-center gap-2">
                            <Bookmark size={16} className="text-orange-500" />
                            <h2 className="text-sm font-semibold text-white">
                                Saved Leads {savedFetched && !savedLoading ? `(${filteredSavedLeads.length})` : ""}
                            </h2>
                        </div>
                        <button
                            type="button"
                            onClick={fetchSavedLeads}
                            disabled={savedLoading}
                            className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-400 hover:text-orange-400 disabled:opacity-50 transition"
                        >
                            <RefreshCw size={13} className={savedLoading ? "animate-spin" : ""} /> Refresh
                        </button>
                    </div>

                    {/* Product filter — same dropdown pattern as the main Product
                        selector in the discovery flow, scoped to this view. */}
                    <div className="mt-3 shrink-0 max-w-md">
                        <label className="text-xs font-semibold text-gray-400 tracking-wide uppercase">Product</label>
                        {productsLoading ? (
                            <div className="flex items-center gap-2 text-gray-500 text-sm mt-2">
                                <Loader2 size={14} className="animate-spin" /> Loading your products...
                            </div>
                        ) : products.length === 0 ? (
                            <div className="mt-2 flex items-center gap-2 text-gray-500 text-sm border border-dashed border-orange-600/30 rounded-xl px-4 py-3">
                                <Package size={15} />
                                No products yet — add one from the Home page first.
                            </div>
                        ) : (
                            <div className="relative mt-2">
                                <select
                                    value={savedProductId}
                                    onChange={(e) => setSavedProductId(e.target.value)}
                                    className="w-full appearance-none bg-black border border-orange-600/30 text-white text-sm rounded-xl pl-4 pr-10 py-2.5 outline-none focus:border-orange-500/60"
                                >
                                    <option value="">All Products</option>
                                    {products.map((p) => (
                                        <option key={p.id} value={p.id}>
                                            {p.product_name}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown size={16} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
                            </div>
                        )}
                    </div>

                    {/* Active / Inactive split — Active = the lead replied (email/WhatsApp/SMS)
                        and the AI read that reply as interested. Inactive = contacted but no
                        reply, or a reply read as not-interested. "All" also shows leads that
                        haven't been contacted yet at all. */}
                    <div className="mt-3 shrink-0 flex items-center gap-1.5 border border-white/10 rounded-xl p-1 w-fit bg-black/40">
                        {[
                            { key: "all", label: `All (${savedInterestCounts.all})` },
                            { key: "active", label: `Active (${savedInterestCounts.active})` },
                            { key: "inactive", label: `Inactive (${savedInterestCounts.inactive})` },
                        ].map((tab) => (
                            <button
                                key={tab.key}
                                type="button"
                                onClick={() => setSavedInterestTab(tab.key)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                                    savedInterestTab === tab.key
                                        ? "bg-orange-500 text-black"
                                        : "text-gray-400 hover:text-white"
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    <div className={`mt-4 flex-1 min-h-0 overflow-y-auto ${THEMED_SCROLLBAR}`}>
                        {savedLoading && (
                            <div className="flex items-center gap-2 text-gray-500 text-sm py-10 justify-center">
                                <Loader2 size={14} className="animate-spin" /> Loading saved leads...
                            </div>
                        )}

                        {!savedLoading && savedError && (
                            <div className="flex items-center gap-2 text-sm text-red-400 border border-red-500/30 bg-red-500/10 rounded-xl px-4 py-3">
                                <AlertCircle size={15} /> {savedError}
                            </div>
                        )}

                        {!savedLoading && !savedError && savedFetched && filteredSavedLeads.length === 0 && (
                            <div className="border border-dashed border-orange-600/30 rounded-2xl py-14 flex flex-col items-center gap-2 text-center px-4">
                                <Users size={28} className="text-gray-600" />
                                <p className="text-gray-500 text-sm max-w-sm">
                                    {savedInterestTab === "active"
                                        ? "No active leads yet — this fills in once a contacted lead replies and the AI reads them as interested."
                                        : savedInterestTab === "inactive"
                                        ? "No inactive leads here."
                                        : savedProductId
                                        ? "No saved leads yet for this product."
                                        : "No saved leads yet. Head to \"Unsaved Leads\" to discover businesses and add them here."}
                                </p>
                            </div>
                        )}

                        {!savedLoading && !savedError && filteredSavedLeads.length > 0 && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3.5">
                                {filteredSavedLeads.map((lead) => (
                                    <div
                                        key={lead.id ?? `${lead.user_name}-${lead.lead_source}`}
                                        className="bg-black border border-orange-600/20 rounded-2xl p-4 flex flex-col gap-2.5"
                                    >
                                        <div className="flex items-start justify-between gap-2">
                                            <h3 className="text-white font-semibold text-sm leading-snug">{lead.user_name}</h3>
                                            {/* No borders/pills — just a small colored dot + label, so
                                                "Facebook" and "Active"/"Inactive" read as plain text tags
                                                instead of boxed badges. */}
                                            <div className="shrink-0 flex flex-col items-end gap-1">
                                                <span className="text-[10px] font-medium tracking-wide uppercase text-gray-400">
                                                    {lead.lead_source}
                                                </span>
                                                {lead.interest_status === "active" && (
                                                    <span className="inline-flex items-center gap-1.5 text-[10px] font-medium tracking-wide uppercase text-green-400">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-green-400 shrink-0" />
                                                        Active
                                                    </span>
                                                )}
                                                {lead.interest_status === "inactive" && (
                                                    <span className="inline-flex items-center gap-1.5 text-[10px] font-medium tracking-wide uppercase text-gray-500">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-gray-500 shrink-0" />
                                                        Inactive
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {lead.user_address && (
                                            <div className="flex items-start gap-1.5 text-xs text-gray-400">
                                                <MapPin size={13} className="text-orange-500 shrink-0 mt-0.5" />
                                                <span className="leading-snug">{lead.user_address}</span>
                                            </div>
                                        )}
                                        {lead.user_mobile_number && (
                                            <div className="flex items-center gap-1.5 text-xs text-gray-400">
                                                <Phone size={13} className="text-orange-500 shrink-0" />
                                                {lead.user_mobile_number}
                                            </div>
                                        )}
                                        {lead.web_url && (
                                            <div className="flex items-center gap-1.5 text-xs text-gray-400 min-w-0">
                                                <Globe size={13} className="text-orange-500 shrink-0" />
                                                <span className="truncate">{lead.web_url}</span>
                                            </div>
                                        )}
                                        <div className="flex items-center justify-between mt-1 pt-2 border-t border-white/5">
                                            <span className="text-[10px] text-gray-500">{lead.current_status || "New"}</span>
                                            {Number.isFinite(Number(lead.ai_score)) && (
                                                <span className="text-[11px] text-orange-400 font-semibold">
                                                    Score: {Math.round(Number(lead.ai_score))}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {activeView === "unsaved" && (
              <>
                <button
                    type="button"
                    onClick={() => setActiveView(null)}
                    className="mt-6 shrink-0 inline-flex items-center gap-1.5 text-xs font-medium text-gray-400 hover:text-orange-400 transition w-fit"
                >
                    <ArrowLeft size={14} /> Back
                </button>

            {/* Product selector */}
            <div className="mt-6 shrink-0 rounded-2xl border border-white/10 bg-black p-5">
                <div className="flex items-center gap-2.5 mb-3">
                    <span className="h-8 w-8 rounded-full bg-orange-500/15 flex items-center justify-center shrink-0">
                        <Package size={15} className="text-orange-500" />
                    </span>
                    <div>
                        <h3 className="text-sm font-semibold text-white">Product</h3>
                        <p className="text-[11px] text-gray-500">Scopes lead discovery to this product's audience</p>
                    </div>
                </div>

                {productsLoading ? (
                    <div className="flex items-center gap-2 text-gray-500 text-sm">
                        <Loader2 size={14} className="animate-spin" /> Loading your products...
                    </div>
                ) : products.length === 0 ? (
                    <div className="flex items-center gap-2 text-gray-500 text-sm border border-dashed border-orange-600/30 rounded-xl px-4 py-3">
                        <Package size={15} />
                        No products yet — add one from the Home page first.
                    </div>
                ) : (
                    <div className="relative max-w-md">
                        <select
                            value={selectedProductId}
                            onChange={(e) => handleSelectProduct(e.target.value)}
                            className="w-full appearance-none bg-black border border-orange-600/30 text-white text-sm rounded-xl pl-4 pr-10 py-2.5 outline-none focus:border-orange-500/60 hover:border-orange-500/40 transition"
                        >
                            {products.map((p) => (
                                <option key={p.id} value={p.id}>
                                    {p.product_name}
                                </option>
                            ))}
                        </select>
                        <ChevronDown size={16} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
                    </div>
                )}
            </div>

            {selectedProduct && (
                <div className="mt-5 shrink-0 rounded-2xl border border-white/10 bg-black p-5">
                    {/* Choose Lead Source(s) */}
                    <div className="flex items-center gap-2.5">
                        <span className="h-8 w-8 rounded-full bg-orange-500/15 flex items-center justify-center shrink-0">
                            <Compass size={15} className="text-orange-500" />
                        </span>
                        <div>
                            <h2 className="text-sm font-semibold text-white">Choose Lead Source</h2>
                            <p className="text-[11px] text-gray-500">
                                Select one or more — AI Search, OpenStreetMap and LinkedIn Companies are live, the rest are on the roadmap
                            </p>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-4">
                        {LEAD_SOURCES.map((source) => {
                            const Icon = source.icon;
                            const checked = selectedSources.has(source.id);
                            return (
                                <button
                                    key={source.id}
                                    type="button"
                                    onClick={() => toggleSource(source)}
                                    disabled={!source.enabled}
                                    className={`relative flex items-center gap-2.5 rounded-xl border px-3.5 py-3 text-left transition ${
                                        !source.enabled
                                            ? "border-white/5 bg-white/[0.02] opacity-50 cursor-not-allowed"
                                            : checked
                                            ? "border-orange-500 bg-orange-500/10"
                                            : "border-orange-600/20 bg-black hover:border-orange-500/50"
                                    }`}
                                >
                                    <span
                                        className={`h-4 w-4 rounded border shrink-0 flex items-center justify-center ${
                                            checked ? "bg-orange-500 border-orange-500" : "border-gray-600"
                                        }`}
                                    >
                                        {checked && <Check size={11} className="text-white" />}
                                    </span>
                                    <Icon size={15} className={checked ? "text-orange-400 shrink-0" : "text-gray-500 shrink-0"} />
                                    <span className={`text-xs font-medium truncate ${checked ? "text-white" : "text-gray-400"}`}>
                                        {source.label}
                                    </span>
                                    {!source.enabled && (
                                        <span className="absolute -top-2 -right-2 flex items-center gap-0.5 text-[9px] font-semibold bg-[#1a1a1a] border border-white/10 text-gray-500 rounded-full px-1.5 py-0.5">
                                            <Lock size={8} /> Soon
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {/* LinkedIn Companies box — appears once the "LinkedIn Companies"
                        checkbox above is selected. No live LinkedIn API is wired up,
                        so the user pastes in company names/URLs copied from LinkedIn's
                        own search results, and each one gets AI-researched on Find Leads. */}
                    {selectedSources.has("linkedin") && (
                        <div className="mt-4 rounded-xl border border-orange-600/20 bg-white/[0.02] p-4">
                            <div className="flex items-center gap-2">
                                <Network size={14} className="text-orange-500" />
                                <label htmlFor="linkedin-companies-box" className="text-xs font-semibold text-white">
                                    LinkedIn Companies
                                </label>
                                {linkedinCompanyNames.length > 0 && (
                                    <span className="text-[10px] text-gray-500">
                                        {linkedinCompanyNames.length} compan{linkedinCompanyNames.length === 1 ? "y" : "ies"}
                                    </span>
                                )}
                            </div>
                            <p className="text-[11px] text-gray-500 mt-1">
                                Paste company names and/or LinkedIn company URLs — one per line, or comma-separated.
                                Each one gets AI-researched (decision maker, industry, contact details) on Find Leads.
                            </p>
                            <textarea
                                id="linkedin-companies-box"
                                value={linkedinCompaniesInput}
                                onChange={(e) => setLinkedinCompaniesInput(e.target.value)}
                                placeholder={"Acme Inc\nlinkedin.com/company/globex-corp\nInitech"}
                                rows={3}
                                className="w-full mt-2.5 bg-black border border-orange-600/30 text-white text-xs rounded-xl px-3.5 py-2.5 outline-none focus:border-orange-500/60 hover:border-orange-500/40 transition resize-y placeholder:text-gray-600"
                            />
                        </div>
                    )}

                    {/* Ready to Search + Find Leads action, same card */}
                    <div className="border-t border-white/10 mt-5 pt-5">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div className="flex items-center gap-2.5">
                                <span className="h-8 w-8 rounded-full bg-orange-500/15 flex items-center justify-center shrink-0">
                                    <Radar size={15} className="text-orange-500" />
                                </span>
                                <div>
                                    <h3 className="text-sm font-semibold text-white">Ready to Search</h3>
                                    <p className="text-[11px] text-gray-500">Targeting details from {selectedProduct.product_name}</p>
                                </div>
                            </div>

                            {/* Small pill button, matches the Generate Leads button style —
                                sits in the card header instead of stretched full-width. */}
                            <button
                                onClick={findLeads}
                                disabled={!canSearch || searchLoading}
                                className="shrink-0 flex items-center justify-center gap-1.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-semibold px-4 py-2 rounded-full transition whitespace-nowrap"
                            >
                                {searchLoading ? (
                                    <Loader2 size={13} className="animate-spin" />
                                ) : (
                                    <Sparkles size={13} />
                                )}
                                {searchLoading ? "Searching..." : "Find Leads"}
                            </button>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                            <div className="rounded-xl border border-white/10 bg-white/[0.02] px-3.5 py-2.5">
                                <p className="text-[10px] text-gray-500 uppercase tracking-wide">Product</p>
                                <p className="text-sm text-white font-medium mt-0.5 truncate">{selectedProduct.product_name}</p>
                            </div>
                            <div className="rounded-xl border border-white/10 bg-white/[0.02] px-3.5 py-2.5">
                                <p className="text-[10px] text-gray-500 uppercase tracking-wide">Industry</p>
                                <p className="text-sm text-white font-medium mt-0.5 truncate">{selectedProduct.category || "—"}</p>
                            </div>
                            <div className="rounded-xl border border-white/10 bg-white/[0.02] px-3.5 py-2.5">
                                <p className="text-[10px] text-gray-500 uppercase tracking-wide">Target</p>
                                <p className="text-sm text-white font-medium mt-0.5 truncate">{selectedProduct.target_audience || "—"}</p>
                            </div>
                            <div className="rounded-xl border border-white/10 bg-white/[0.02] px-3.5 py-2.5">
                                <p className="text-[10px] text-gray-500 uppercase tracking-wide">Location</p>
                                <p className="text-sm text-white font-medium mt-0.5 truncate">{selectedProduct.target_location || "—"}</p>
                            </div>
                        </div>

                        {!canSearch && (
                            <p className="text-[11px] text-gray-600 mt-3">
                                {selectedSources.size === 0
                                    ? "Select at least one lead source above."
                                    : selectedSources.has("linkedin") && linkedinCompanyNames.length === 0
                                    ? "Add at least one LinkedIn company name or URL above."
                                    : "Select at least one lead source above."}
                            </p>
                        )}
                    </div>
                </div>
            )}

            {/* Sector / Category / Subcategory hierarchy filter + its own results —
                independent of the Product above: picking a Product still scopes the
                "Find Leads" flow below, but this filter generates/searches leads purely
                by Sector/Category/Subcategory, with no product involved. */}
            <div className="mt-5 shrink-0">
                <HierarchyFilter />

                {/* AI-generated leads from the filter itself (Generate Leads button) */}
                {filterProspects.length > 0 && (
                    <div className="border border-orange-600/20 rounded-2xl mb-4 overflow-hidden">
                        <div className="flex items-center gap-2 px-4 pt-4">
                            <Bot size={15} className="text-orange-500" />
                            <h3 className="text-sm font-semibold text-white">
                                AI-Generated Leads ({filterProspects.length})
                            </h3>
                        </div>
                        <p className="text-[11px] text-amber-500/80 flex items-start gap-1.5 px-4 mt-1.5 mb-3">
                            <Info size={12} className="shrink-0 mt-0.5" />
                            AI-generated from the model's general knowledge — not verified real-time data. Confirm contact details before outreach.
                        </p>
                        <div
                            className={`overflow-x-auto overflow-y-hidden max-h-[520px] ${THEMED_SCROLLBAR} ${HORIZONTAL_SCROLLBAR}`}
                        >
                            <table className="w-full text-xs min-w-[1100px]">
                                <thead className="sticky top-0 z-20 bg-black">
                                    <tr className="text-left text-gray-500 border-b border-orange-600/20">
                                        {visibleFilterFields.map((f) => (
                                            <th
                                                key={f.key}
                                                className={`px-3 py-2.5 font-medium whitespace-nowrap ${
                                                    f.key === "company_name" ? "sticky left-0 z-10 bg-black" : ""
                                                }`}
                                            >
                                                {f.label}
                                            </th>
                                        ))}
                                        <th className="px-3 py-2.5"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filterProspects.map((p, index) => {
                                        const key = `filter-${index}-${p.company_name}`;
                                        const isAdded = addedKeys.has(key);
                                        const isAdding = addingKey === key;
                                        return (
                                            <tr key={key} className="border-b border-white/5 last:border-0 align-top">
                                                {visibleFilterFields.map((f) => (
                                                    <td
                                                        key={f.key}
                                                        className={`px-3 py-2.5 text-gray-300 max-w-[220px] ${
                                                            f.key === "company_name" ? "sticky left-0 z-10 bg-black" : ""
                                                        }`}
                                                    >
                                                        {f.key === "website" && p.website ? (
                                                            <VerifiedWebsiteLink
                                                                url={p.website.startsWith("http") ? p.website : `https://${p.website}`}
                                                                className="text-orange-400 hover:underline flex items-center gap-1"
                                                            />
                                                        ) : f.key === "company_name" ? (
                                                            <span className="text-white font-medium">{p[f.key] || "—"}</span>
                                                        ) : f.key === "lead_score" ? (
                                                            <span className="text-orange-400 font-semibold">{p[f.key] ?? "—"}</span>
                                                        ) : (
                                                            <span className="line-clamp-3">{p[f.key] || "—"}</span>
                                                        )}
                                                    </td>
                                                ))}
                                                <td className="px-3 py-2.5">
                                                    <button
                                                        onClick={() => addFilterProspectToCrm(p, index)}
                                                        disabled={isAdding || isAdded}
                                                        className={`flex items-center justify-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-lg transition whitespace-nowrap ${
                                                            isAdded
                                                                ? "bg-green-500/15 border border-green-500/30 text-green-400 cursor-default"
                                                                : "border border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white disabled:opacity-60"
                                                        }`}
                                                    >
                                                        {isAdded ? (
                                                            <>
                                                                <Check size={12} /> Added
                                                            </>
                                                        ) : isAdding ? (
                                                            <Loader2 size={12} className="animate-spin" />
                                                        ) : (
                                                            "Add to CRM"
                                                        )}
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

            </div>

            {/* Results */}
            <div className="mt-6 flex-1 min-h-0">
                {searchError && (
                    <div className="flex items-center gap-2 text-sm text-red-400 border border-red-500/30 bg-red-500/10 rounded-xl px-4 py-3 mb-4">
                        <AlertCircle size={15} /> {searchError}
                    </div>
                )}

                {!selectedProduct && !productsLoading && products.length > 0 && (
                    <div className="border border-dashed border-orange-600/30 rounded-2xl py-14 flex flex-col items-center gap-2 text-center px-4">
                        <Package size={28} className="text-gray-600" />
                        <p className="text-gray-500 text-sm">Pick a product above to get started.</p>
                    </div>
                )}

                {hasSearched &&
                    !searchLoading &&
                    !searchError &&
                    discovered.length === 0 &&
                    prospects.length === 0 &&
                    linkedinProspects.length === 0 && (
                    <div className="border border-dashed border-orange-600/30 rounded-2xl py-14 flex flex-col items-center gap-2 text-center px-4">
                        <Package size={28} className="text-gray-600" />

                        <p className="text-gray-500 text-sm max-w-sm">
                            No matches found yet for this search. Try a broader target location on the product, or check back later.
                        </p>
                    </div>
                )}

                {/* AI Search prospects — AI-inferred, richer fields, clearly disclaimed */}
                {prospects.length > 0 && (
                    <div className="mb-8">
                        <div className="flex items-center gap-2 mb-2">
                            <Bot size={15} className="text-orange-500" />
                            <h3 className="text-sm font-semibold text-white">AI Search Prospects ({prospects.length})</h3>
                        </div>
                        <p className="text-[11px] text-amber-500/80 flex items-start gap-1.5 mb-3">
                            <Info size={12} className="shrink-0 mt-0.5" />
                            AI-generated from the model's general knowledge — not verified real-time data. Treat as research starting points; confirm contact details before outreach. This Lead Score carries through unchanged when you add a lead to the CRM.
                        </p>
                        <div className={`border border-orange-600/20 rounded-2xl max-h-[520px] overflow-x-auto overflow-y-hidden ${THEMED_SCROLLBAR} ${HORIZONTAL_SCROLLBAR}`}>
                            <table className="w-full text-xs min-w-[1100px]">
                                <thead className="sticky top-0 z-20 bg-black">
                                    <tr className="text-left text-gray-500 border-b border-orange-600/20">
                                        {visibleProspectFields.map((f) => (
                                            <th
                                                key={f.key}
                                                className={`px-3 py-2.5 font-medium whitespace-nowrap ${
                                                    f.key === "company_name" ? "sticky left-0 z-10 bg-black" : ""
                                                }`}
                                            >
                                                {f.label}
                                            </th>
                                        ))}
                                        <th className="px-3 py-2.5"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {prospects.map((p, index) => {
                                        const key = `gpt-${index}-${p.company_name}`;
                                        const isAdded = addedKeys.has(key);
                                        const isAdding = addingKey === key;
                                        return (
                                            <tr key={key} className="border-b border-white/5 last:border-0 align-top">
                                                {visibleProspectFields.map((f) => (
                                                    <td
                                                        key={f.key}
                                                        className={`px-3 py-2.5 text-gray-300 max-w-[220px] ${
                                                            f.key === "company_name" ? "sticky left-0 z-10 bg-black" : ""
                                                        }`}
                                                    >
                                                        {f.key === "website" && p.website ? (
                                                            <VerifiedWebsiteLink
                                                                url={p.website.startsWith("http") ? p.website : `https://${p.website}`}
                                                                className="text-orange-400 hover:underline flex items-center gap-1"
                                                            />
                                                        ) : f.key === "company_name" ? (
                                                            <span className="text-white font-medium">{p[f.key] || "—"}</span>
                                                        ) : f.key === "lead_score" ? (
                                                            <span className="text-orange-400 font-semibold">{p[f.key] ?? "—"}</span>
                                                        ) : (
                                                            <span className="line-clamp-3">{p[f.key] || "—"}</span>
                                                        )}
                                                    </td>
                                                ))}
                                                <td className="px-3 py-2.5">
                                                    <button
                                                        onClick={() => addProspectToCrm(p, index)}
                                                        disabled={isAdding || isAdded}
                                                        className={`flex items-center justify-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-lg transition whitespace-nowrap ${
                                                            isAdded
                                                                ? "bg-green-500/15 border border-green-500/30 text-green-400 cursor-default"
                                                                : "border border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white disabled:opacity-60"
                                                        }`}
                                                    >
                                                        {isAdded ? (
                                                            <>
                                                                <Check size={12} /> Added
                                                            </>
                                                        ) : isAdding ? (
                                                            <Loader2 size={12} className="animate-spin" />
                                                        ) : (
                                                            "Add to CRM"
                                                        )}
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* LinkedIn Companies results — AI-researched from the company
                    names/URLs the user pasted in, same shape/disclaimer as AI Search. */}
                {linkedinProspects.length > 0 && (
                    <div className="mb-8">
                        <div className="flex items-center gap-2 mb-2">
                            <Network size={15} className="text-orange-500" />
                            <h3 className="text-sm font-semibold text-white">LinkedIn Companies ({linkedinProspects.length})</h3>
                        </div>
                        <p className="text-[11px] text-amber-500/80 flex items-start gap-1.5 mb-3">
                            <Info size={12} className="shrink-0 mt-0.5" />
                            AI-researched from the model's general knowledge of the companies you listed — not a live LinkedIn lookup. Confirm contact details before outreach.
                        </p>
                        <div className={`border border-orange-600/20 rounded-2xl max-h-[520px] overflow-x-auto overflow-y-hidden ${THEMED_SCROLLBAR} ${HORIZONTAL_SCROLLBAR}`}>
                            <table className="w-full text-xs min-w-[1100px]">
                                <thead className="sticky top-0 z-20 bg-black">
                                    <tr className="text-left text-gray-500 border-b border-orange-600/20">
                                        {visibleLinkedinFields.map((f) => (
                                            <th
                                                key={f.key}
                                                className={`px-3 py-2.5 font-medium whitespace-nowrap ${
                                                    f.key === "company_name" ? "sticky left-0 z-10 bg-black" : ""
                                                }`}
                                            >
                                                {f.label}
                                            </th>
                                        ))}
                                        <th className="px-3 py-2.5"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {linkedinProspects.map((p, index) => {
                                        const key = `linkedin-${index}-${p.company_name}`;
                                        const isAdded = addedKeys.has(key);
                                        const isAdding = addingKey === key;
                                        return (
                                            <tr key={key} className="border-b border-white/5 last:border-0 align-top">
                                                {visibleLinkedinFields.map((f) => (
                                                    <td
                                                        key={f.key}
                                                        className={`px-3 py-2.5 text-gray-300 max-w-[220px] ${
                                                            f.key === "company_name" ? "sticky left-0 z-10 bg-black" : ""
                                                        }`}
                                                    >
                                                        {f.key === "website" && p.website ? (
                                                            <VerifiedWebsiteLink
                                                                url={p.website.startsWith("http") ? p.website : `https://${p.website}`}
                                                                className="text-orange-400 hover:underline flex items-center gap-1"
                                                            />
                                                        ) : f.key === "company_name" ? (
                                                            <span className="text-white font-medium">{p[f.key] || "—"}</span>
                                                        ) : f.key === "lead_score" ? (
                                                            <span className="text-orange-400 font-semibold">{p[f.key] ?? "—"}</span>
                                                        ) : (
                                                            <span className="line-clamp-3">{p[f.key] || "—"}</span>
                                                        )}
                                                    </td>
                                                ))}
                                                <td className="px-3 py-2.5">
                                                    <button
                                                        onClick={() => addLinkedinProspectToCrm(p, index)}
                                                        disabled={isAdding || isAdded}
                                                        className={`flex items-center justify-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-lg transition whitespace-nowrap ${
                                                            isAdded
                                                                ? "bg-green-500/15 border border-green-500/30 text-green-400 cursor-default"
                                                                : "border border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white disabled:opacity-60"
                                                        }`}
                                                    >
                                                        {isAdded ? (
                                                            <>
                                                                <Check size={12} /> Added
                                                            </>
                                                        ) : isAdding ? (
                                                            <Loader2 size={12} className="animate-spin" />
                                                        ) : (
                                                            "Add to CRM"
                                                        )}
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* OpenStreetMap results — real, verified-to-exist places */}
                {discovered.length > 0 && (
                    <div>
                        <div className="flex items-center gap-2 mb-3">
                            <MapIcon size={15} className="text-orange-500" />
                            <h3 className="text-sm font-semibold text-white">OpenStreetMap Results ({discovered.length})</h3>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3.5">
                            {discovered.map((lead) => {
                                const key = String(lead.osm_id ?? lead.user_name);
                                const isAdded = addedKeys.has(key);
                                const isAdding = addingKey === key;
                                return (
                                    <div
                                        key={key}
                                        className="bg-black border border-orange-600/20 rounded-2xl p-4 flex flex-col gap-2.5"
                                    >
                                        <div className="flex items-start justify-between gap-2">
                                            <h3 className="text-white font-semibold text-sm leading-snug">{lead.user_name}</h3>
                                            <span className="shrink-0 text-[10px] bg-white/5 border border-white/10 text-gray-400 rounded-full px-2 py-0.5">
                                                OpenStreetMap
                                            </span>
                                        </div>

                                        {lead.user_address && (
                                            <div className="flex items-start gap-1.5 text-xs text-gray-400">
                                                <MapPin size={13} className="text-orange-500 shrink-0 mt-0.5" />
                                                <span className="leading-snug">{lead.user_address}</span>
                                            </div>
                                        )}
                                        {lead.user_mobile_number && (
                                            <div className="flex items-center gap-1.5 text-xs text-gray-400">
                                                <Phone size={13} className="text-orange-500 shrink-0" />
                                                {lead.user_mobile_number}
                                            </div>
                                        )}
                                        {lead.web_url && (
                                            <div className="flex items-center gap-1.5 text-xs text-gray-400 min-w-0">
                                                <Globe size={13} className="text-orange-500 shrink-0" />
                                                <span className="truncate">{lead.web_url}</span>
                                            </div>
                                        )}
                                        {!lead.user_mobile_number && !lead.web_url && (
                                            <p className="text-[11px] text-gray-600 italic">No contact details mapped for this listing.</p>
                                        )}

                                        <button
                                            onClick={() => addOsmLeadToCrm(lead)}
                                            disabled={isAdding || isAdded}
                                            className={`mt-1.5 flex items-center justify-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg transition ${
                                                isAdded
                                                    ? "bg-green-500/15 border border-green-500/30 text-green-400 cursor-default"
                                                    : "border border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white disabled:opacity-60"
                                            }`}
                                        >
                                            {isAdded ? (
                                                <>
                                                    <Check size={13} /> Added to CRM
                                                </>
                                            ) : isAdding ? (
                                                <>
                                                    <Loader2 size={13} className="animate-spin" /> Adding...
                                                </>
                                            ) : (
                                                "Add to CRM"
                                            )}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* Add Lead Manually modal — hierarchy-aware sibling of "Add to CRM" above */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                    <div className="bg-black border border-orange-500/30 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-4 border-b border-orange-500/20">
                            <h2 className="text-lg font-semibold text-white">Add Lead Manually</h2>
                            <button
                                type="button"
                                onClick={() => setShowAddModal(false)}
                                className="text-gray-400 hover:text-white"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="p-4 space-y-3">
                            {searchError && (
                                <div className="flex items-center gap-2 text-xs text-red-400 border border-red-500/30 bg-red-500/10 rounded-lg px-3 py-2">
                                    <AlertCircle size={13} /> {searchError}
                                </div>
                            )}

                            <div>
                                <label className="text-xs font-semibold text-gray-400 mb-1 block">
                                    Company Name *
                                </label>
                                <input
                                    type="text"
                                    value={manualLeadForm.company_name}
                                    onChange={(e) => setManualLeadForm({ ...manualLeadForm, company_name: e.target.value })}
                                    className="w-full bg-black border border-orange-500/30 rounded-lg px-3 py-2 text-white text-sm"
                                    placeholder="Enter company name"
                                />
                            </div>

                            {/* Hierarchy Selects */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div>
                                    <label className="text-xs font-semibold text-gray-400 mb-1 block">Sector</label>
                                    <select
                                        value={manualLeadForm.sector_id || ""}
                                        onChange={(e) =>
                                            setManualLeadForm({
                                                ...manualLeadForm,
                                                sector_id: e.target.value ? parseInt(e.target.value, 10) : null,
                                                category_id: null,
                                                subcategory_id: null,
                                            })
                                        }
                                        className="w-full bg-black border border-orange-500/30 rounded-lg px-3 py-2 text-white text-xs"
                                    >
                                        <option value="">-- Select Sector --</option>
                                        {sectors.map((s) => (
                                            <option key={s.id} value={s.id}>{s.sector_name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="text-xs font-semibold text-gray-400 mb-1 block">Category</label>
                                    <select
                                        value={manualLeadForm.category_id || ""}
                                        onChange={(e) =>
                                            setManualLeadForm({
                                                ...manualLeadForm,
                                                category_id: e.target.value ? parseInt(e.target.value, 10) : null,
                                                subcategory_id: null,
                                            })
                                        }
                                        disabled={!manualLeadForm.sector_id}
                                        className="w-full bg-black border border-orange-500/30 rounded-lg px-3 py-2 text-white text-xs disabled:opacity-50"
                                    >
                                        <option value="">-- Select Category --</option>
                                        {categories.map((c) => (
                                            <option key={c.id} value={c.id}>{c.category_name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="text-xs font-semibold text-gray-400 mb-1 block">Subcategory</label>
                                    <select
                                        value={manualLeadForm.subcategory_id || ""}
                                        onChange={(e) =>
                                            setManualLeadForm({
                                                ...manualLeadForm,
                                                subcategory_id: e.target.value ? parseInt(e.target.value, 10) : null,
                                            })
                                        }
                                        disabled={!manualLeadForm.category_id}
                                        className="w-full bg-black border border-orange-500/30 rounded-lg px-3 py-2 text-white text-xs disabled:opacity-50"
                                    >
                                        <option value="">-- Select Subcategory --</option>
                                        {subcategories.map((sc) => (
                                            <option key={sc.id} value={sc.id}>{sc.subcategory_name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Contact Details */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-semibold text-gray-400 mb-1 block">Phone</label>
                                    <input
                                        type="tel"
                                        value={manualLeadForm.phone}
                                        onChange={(e) => setManualLeadForm({ ...manualLeadForm, phone: e.target.value })}
                                        className="w-full bg-black border border-orange-500/30 rounded-lg px-3 py-2 text-white text-sm"
                                        placeholder="Phone"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-gray-400 mb-1 block">Email</label>
                                    <input
                                        type="email"
                                        value={manualLeadForm.email}
                                        onChange={(e) => setManualLeadForm({ ...manualLeadForm, email: e.target.value })}
                                        className="w-full bg-black border border-orange-500/30 rounded-lg px-3 py-2 text-white text-sm"
                                        placeholder="Email"
                                    />
                                </div>
                            </div>

                            <input
                                type="text"
                                value={manualLeadForm.decision_maker}
                                onChange={(e) => setManualLeadForm({ ...manualLeadForm, decision_maker: e.target.value })}
                                className="w-full bg-black border border-orange-500/30 rounded-lg px-3 py-2 text-white text-sm"
                                placeholder="Decision Maker"
                            />

                            <input
                                type="text"
                                value={manualLeadForm.designation}
                                onChange={(e) => setManualLeadForm({ ...manualLeadForm, designation: e.target.value })}
                                className="w-full bg-black border border-orange-500/30 rounded-lg px-3 py-2 text-white text-sm"
                                placeholder="Designation"
                            />

                            <select
                                value={manualLeadForm.company_size}
                                onChange={(e) => setManualLeadForm({ ...manualLeadForm, company_size: e.target.value })}
                                className="w-full bg-black border border-orange-500/30 rounded-lg px-3 py-2 text-white text-sm"
                            >
                                <option value="">-- Company Size --</option>
                                <option value="1-10">1-10</option>
                                <option value="11-50">11-50</option>
                                <option value="51-200">51-200</option>
                                <option value="201-1000">201-1000</option>
                                <option value="1000+">1000+</option>
                            </select>

                            <textarea
                                value={manualLeadForm.pain_points}
                                onChange={(e) => setManualLeadForm({ ...manualLeadForm, pain_points: e.target.value })}
                                className="w-full bg-black border border-orange-500/30 rounded-lg px-3 py-2 text-white text-sm h-20 resize-none"
                                placeholder="Pain Points"
                            />

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <input
                                    type="text"
                                    value={manualLeadForm.location}
                                    onChange={(e) => setManualLeadForm({ ...manualLeadForm, location: e.target.value })}
                                    className="w-full bg-black border border-orange-500/30 rounded-lg px-3 py-2 text-white text-sm"
                                    placeholder="Location"
                                />
                                <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={manualLeadForm.lead_score}
                                    onChange={(e) => setManualLeadForm({ ...manualLeadForm, lead_score: parseInt(e.target.value, 10) || 0 })}
                                    className="w-full bg-black border border-orange-500/30 rounded-lg px-3 py-2 text-white text-sm"
                                    placeholder="Lead Score (0-100)"
                                />
                            </div>
                        </div>

                        <div className="flex gap-2 p-4 border-t border-orange-500/20">
                            <button
                                type="button"
                                onClick={() => setShowAddModal(false)}
                                className="flex-1 px-4 py-2 rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-900 text-sm font-semibold"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleAddManualLead}
                                disabled={addingManualLead}
                                className="flex-1 px-4 py-2 rounded-lg bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50 text-sm font-semibold flex items-center justify-center gap-2"
                            >
                                {addingManualLead ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                                {addingManualLead ? "Adding..." : "Add Lead"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
              </>
            )}
        </div>
    );
};

export default LeadGenerationSection;